import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import PDPFooterPrice from '@allesauto/core/footerPrice';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import Checkmark from '@allesauto/design/svg/checkmark.svg';
import { PaymentType, ProductType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import {
  FinanceTypeEn,
  OVERLAY_ADD_PADDING_CLASSNAME,
  SupportedCurrencies,
  ToastifyType,
} from '@allesauto/utils/consts';
import { isEmptyObject } from '@allesauto/utils/funcs';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { useScrollingDirection } from '@allesauto/utils/hooks';
import {
  Calculator16,
  Email20,
  Pen20,
  ShoppingBag24,
} from '@carbon/icons-react';
import clsx from 'clsx';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import C2COverlayContact from '@/components/C2C/Overlay/C2COverlayContact';
import ContactDealerForm from '@/components/forms/contactDealerForm/ContactDealerForm';
import PaymentOptions from '@/components/PaymentOptions/PaymentOptions';

import { WL_INTEGRATION_WINTER_LAUSITZ } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import OrderVehicleFormContainer from '@/global/containers/OrderVehicleFormContainer';
import { useFormatedAddToCart } from '@/global/hooks/useFormatedAddToCart';
import { useIsProductInCart } from '@/global/hooks/useIsProductInCart';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';
import { setVehicle } from '@/store/redux/slices/pages/pdp';

import { CustomerDetails } from '../carDealerDetails/CarDealerDetails';

export interface CarDetailsFooterProps {
  vehicleId: string;
  carModel: string;
  carManufacturer: string;
  contactPerson?: SchemaTypes.ContactPerson;
  payment: PaymentType;
  price: number;
  monthlyPrice?: number;
  cashOptions?: SchemaTypes.CashOption[];
  financing?: {
    downPayment?: number;
    duration?: number;
    mileage?: number;
    finalInstallment?: number;
  };
  financingDetails: SchemaTypes.FinancingDetail[];
  financeCalculatorOptions?: SchemaTypes.FinanceCalculatorOptions | null;
  isPrivateSeller?: boolean | null;
  isFinanceLoading?: boolean;
  isFixedPrice?: boolean | null;
  isOrderVehicle?: boolean | null;
  isInteractive: boolean;
  productType?: ProductType;
  dealerId: string;
  dealerName: string;
  sku: string;
  vehicleCondition?: Maybe<string> | undefined;
  bodyColor?: Maybe<string> | undefined;
  customerDetails?: CustomerDetails;
  isConfigurableCar?: boolean;
  carConfiguratorId?: string;
  openConfigurator?: () => void;
  openConfiguratorDealerSelection?: () => void;
  configuratorDealerId?: string;
  dealerDetails?: {
    contactPerson?: SchemaTypes.ContactPerson | null;
    location?: SchemaTypes.Dealer['location'];
    id?: string;
    name?: string | null;
  };
  vehicleSku: string;
  vehiclePrice?: number;
  priceLabelText: string | undefined;
  downPaymentString: string | undefined;
  durationString: string | undefined;
  mileageString: string | undefined;
  finalInstallmentString: string | undefined;
  setCalculateLoanSummary:
    | React.Dispatch<React.SetStateAction<string>>
    | undefined;
}

const CarDetailsFooter: FC<React.PropsWithChildren<CarDetailsFooterProps>> = ({
  vehicleId,
  carManufacturer,
  carModel,
  contactPerson,
  payment,
  price,
  monthlyPrice,
  cashOptions,
  financing,
  financingDetails,
  financeCalculatorOptions,
  isPrivateSeller,
  isFixedPrice,
  isInteractive,
  productType = ProductType.Vehicle,
  dealerId,
  dealerName,
  sku,
  vehicleCondition,
  bodyColor,
  isOrderVehicle,
  isFinanceLoading,
  customerDetails,
  carConfiguratorId,
  isConfigurableCar,
  openConfigurator,
  openConfiguratorDealerSelection,
  configuratorDealerId,
  dealerDetails,
  vehicleSku,
  vehiclePrice = 0,
  priceLabelText,
  downPaymentString,
  durationString,
  mileageString,
  finalInstallmentString,
  setCalculateLoanSummary,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const scrollingDirection = useScrollingDirection();
  const whitelabel = useWhiteLabel();
  const { isProductInCart } = useIsProductInCart();

  const isWinterInstance =
    whitelabel?.merchantOrigin === WL_INTEGRATION_WINTER_LAUSITZ;

  const { monthlyInstallment, downPayment, duration, mileage } = useSelector(
    (state: any) => state.pages.pdp.vehicle.financing || { type: null }
  );

  const vehicleIsAlreadyInCart = isProductInCart(sku);

  const { loading: addToCartLoading, onAddToCartClick } =
    useFormatedAddToCart();

  const pushCarToDataLayer = () => {
    pushAnalyticsEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: SupportedCurrencies.EURO,
        value: price,
        items: [
          {
            item_name: `${carManufacturer?.toLowerCase()} ${carModel?.toLowerCase()}`,
            item_id: vehicleId,
            price: price,
            item_brand: carManufacturer?.toLowerCase(),
            item_category: 'car',
            item_category2: vehicleCondition?.toLowerCase(),
            item_variant: bodyColor?.toLowerCase(),
            item_list_name: 'cart',
            item_list_id: 'cart',
            index: 0,
            quantity: 1,
          },
        ],
      },
    });
  };

  const handleAddToCart = useCallback(async () => {
    await onAddToCartClick({
      dealerId,
      paymentType: payment,
      productType,
      sku,
      financing,
    });
    pushCarToDataLayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, financing, onAddToCartClick, payment, productType, sku]);

  const showPaymentChangeSuccessToast = useCallback(() => {
    import('@allesauto/core/toastify').then((module) => {
      module.toastifyFactory(
        ToastifyType.SUCCESS,
        translate(intl, 'ui.PDP.FOOTER.FINANCE_TYPE_UPDATED.SUCCESS_TITLE'),
        translate(intl, 'ui.PDP.FOOTER.FINANCE_TYPE_UPDATED.SUCCESS_MESSAGE')
      );
    });
  }, [intl]);

  const onPaymentCashSubmit = useCallback(
    (price: number) => {
      // dataLayer push
      pushAnalyticsEvent({
        event: 'choose_payment_type',
        form_process: 'payment_formFinish',
        payment_type: 'Barkauf',
        product_name: `${carManufacturer} ${carModel}`,
        product_id: vehicleId,
        price,
      });

      dispatch(
        setVehicle({ price, financing: undefined, persistentState: true })
      );
      dispatch(closeOverlay());

      if (!whitelabel) {
        // AAA-2902
        showPaymentChangeSuccessToast();
      }
    },
    [
      carManufacturer,
      carModel,
      dispatch,
      showPaymentChangeSuccessToast,
      vehicleId,
      whitelabel,
    ]
  );

  const onPaymentLeasingSubmit = useCallback(
    (values: any) => {
      // dataLayer push
      pushAnalyticsEvent({
        ...values,
        event: 'choose_payment_type',
        form_process: 'payment_formFinish',
        payment_type: 'Leasing',
        product_name: `${carManufacturer} ${carModel}`,
        product_id: vehicleId,
      });

      dispatch(
        setVehicle({
          financing: {
            ...values,
            type: PaymentType.LeasingRateRange,
          },
          persistentState: true,
        })
      );

      dispatch(closeOverlay());

      if (!whitelabel && financing?.duration !== undefined) {
        // AAA-2902
        showPaymentChangeSuccessToast();
      }
    },
    [
      carManufacturer,
      carModel,
      dispatch,
      showPaymentChangeSuccessToast,
      vehicleId,
      whitelabel,
      financing,
    ]
  );

  const onPaymentFinancingSubmit = useCallback(
    (values: any) => {
      // dataLayer push
      pushAnalyticsEvent({
        ...values,
        event: 'choose_payment_type',
        form_process: 'payment_formFinish',
        payment_type: 'Finanzierung',
        product_name: `${carManufacturer} ${carModel}`,
        product_id: vehicleId,
      });

      dispatch(
        setVehicle({
          financing: { ...values, type: PaymentType.LoanRateRange },
          persistentState: true,
        })
      );

      dispatch(closeOverlay());

      if (!whitelabel && financing?.duration !== undefined) {
        // AAA-2902
        showPaymentChangeSuccessToast();
      }
    },
    [
      carManufacturer,
      carModel,
      dispatch,
      showPaymentChangeSuccessToast,
      vehicleId,
      whitelabel,
      financing,
    ]
  );

  const handleBackToSearch = useCallback(() => {
    dispatch(closeOverlay());
    return router.back();
  }, [dispatch, router]);

  // C2C Variation for PDP Footer CTA
  const openContactPrivateSellerForm = useCallback(
    () => {
      dispatch(
        openOverlay({
          children: (
            <C2COverlayContact
              make={carManufacturer ?? ''}
              model={carModel ?? ''}
              dealerName={dealerName}
              c2cConcreteId={sku}
              contactPerson={contactPerson}
            />
          ),
          title: translate(intl, 'ui.FORMS.CONTACT_DEALER.TITLE'), // todo: change title
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { name = '', id = '' } = dealerDetails ?? {};

  const contactPersonDataForm = useMemo(
    () => ({
      firstName: contactPerson?.firstName || '',
      lastName: contactPerson?.lastName || '',
      phone: contactPerson?.phone || '',
    }),
    [contactPerson]
  );

  const contactProps = {
    children: (
      <ContactDealerForm
        dealerId={id}
        vehicleSku={vehicleSku}
        darkBg={false}
        carModel={carModel}
        contactPerson={contactPersonDataForm}
        carManufacturer={carManufacturer}
        dealerName={name ?? ''}
        onBackToSearchClick={handleBackToSearch}
        customerDetails={customerDetails}
        paymentType={payment}
        financeCalculationResult={{
          monthlyInstallment,
          downPayment,
          duration,
          mileage,
        }}
        vehiclePrice={vehiclePrice}
      />
    ),
    title: translate(intl, 'ui.FORMS.CONTACT_DEALER.TITLE'),
  };

  const openOrderVehicleForm = useCallback(() => {
    dispatch(
      openOverlay({
        children: (
          <OrderVehicleFormContainer
            vehicleId={sku}
            dealerId={dealerId}
            carModel={carModel}
            carManufacturer={carManufacturer}
            contactPerson={contactPerson}
            onBackToSearchClick={handleBackToSearch}
            dealerName={dealerName ?? ''}
            customerDetails={customerDetails}
            vehiclePrice={parseInt(`${price}`, 10)}
            paymentType={payment}
          />
        ),
        title: translate(intl, 'ui.FORMS.CONTACT_DEALER.REQUEST_VEHICLE_TITLE'),
      })
    );
  }, [
    dispatch,
    sku,
    dealerId,
    carModel,
    carManufacturer,
    contactPerson,
    handleBackToSearch,
    dealerName,
    customerDetails,
    price,
    payment,
    intl,
  ]);

  const handlerButtonClick = () => {
    if (isConfigurableCar && carConfiguratorId && configuratorDealerId) {
      return openOrderVehicleForm();
    }

    if (isPrivateSeller) {
      return openContactPrivateSellerForm();
    }

    if (isOrderVehicle) {
      return dispatch(openOverlay(contactProps));
    }

    if (vehicleIsAlreadyInCart) {
      return undefined;
    }

    if (isConfigurableCar && !carConfiguratorId && openConfigurator) {
      return openConfigurator();
    }

    if (
      isConfigurableCar &&
      carConfiguratorId &&
      !configuratorDealerId &&
      openConfiguratorDealerSelection
    ) {
      return openConfiguratorDealerSelection();
    }

    return handleAddToCart();
  };

  const ctaPartial = isPrivateSeller ? (
    <span className="font-weight-bold text-center wl-text-color">
      {translate(intl, 'ui.PDP.C2C.CONTACT_SELLER')}
    </span>
  ) : isOrderVehicle || (isConfigurableCar && carConfiguratorId) ? (
    <>
      <Email20 className="wl-text-color" />
      <span className="font-weight-bold pl-10px wl-text-color">
        {translate(intl, 'ui.PDP.FOOTER.REQUEST_VEHICLE_TITLE')}
      </span>
    </>
  ) : isConfigurableCar && !carConfiguratorId ? (
    <>
      <Pen20 className="wl-text-color" />
      <span className="font-weight-bold pl-10px wl-text-color">
        {translate(intl, 'ui.PDP.CAR_CONFIGURATOR.CTA_LABEL')}
      </span>
    </>
  ) : vehicleIsAlreadyInCart ? (
    <>
      <Checkmark className="w-20px wl-text-color" />
      <span className="font-weight-bold pl-10px wl-text-color">
        {translate(intl, 'ui.PDP.FOOTER.ALREADY_IN_SHOPPING_CART')}
      </span>
    </>
  ) : (
    <>
      <ShoppingBag24 className="wl-text-color" />
      <span className="font-weight-bold pl-10px wl-text-color">
        {translate(intl, 'ui.PDP.FOOTER.SHOPPING_BAG')}
      </span>
    </>
  );

  // whitelabel header has a different height, so to place footer under it we should use different top values
  // also see packages/allesauto-core/src/lib/header/index.tsx
  const footerScrollTopClassName = clsx(whitelabel ? 'top-[70px]' : 'top-60px');

  const className = clsx(
    'flex-row grid grid-cols-24 left-0 w-full z-40 md:bottom-0 md:fixed md:h-100px md:top-[unset]',
    `bg-purple80 monochrome:bg-grey90 ${
      isWinterInstance ? 'wl-bg-highlight' : ''
    }`,
    'duration-300 ease-linear transition-all md:transition-none',
    isInteractive ? 'relative' : 'sticky',
    !isInteractive && scrollingDirection !== 'up' && '-top-[70px]',
    !isInteractive && scrollingDirection === 'up' && footerScrollTopClassName,
    isInteractive && payment === PaymentType.CashRange && 'h-[88px]',
    isInteractive &&
      (payment === PaymentType.LoanRateRange ||
        payment === PaymentType.LeasingRateRange) &&
      'h-[208px]',
    OVERLAY_ADD_PADDING_CLASSNAME
  );

  useEffect(
    () => {
      if (
        router.query.financeType === FinanceTypeEn.FINANCING &&
        !isFinanceLoading &&
        !isEmptyObject(financeCalculatorOptions) &&
        payment
      ) {
        dispatch(
          openOverlay({
            title: translate(intl, 'ui.PAYMENT_OPTIONS.TITLE'),
            children: (
              <PaymentOptions
                vehicleId={vehicleId}
                vehiclePrice={price}
                financingDetails={financingDetails}
                selectedPaymentType={payment}
                cashOptions={cashOptions}
                financeCalculatorOptions={financeCalculatorOptions}
                onCashSubmit={onPaymentCashSubmit}
                onLeasingSubmit={onPaymentLeasingSubmit}
                onFinancingSubmit={onPaymentFinancingSubmit}
                setCalculateLoanSummary={setCalculateLoanSummary}
              />
            ),
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, isFinanceLoading, financeCalculatorOptions, payment]
  );

  return (
    <div className={className} data-hide-for-print="">
      <div className="!justify-between col-end-24 col-start-2 flex flex-1 flex-row py-[1.25rem] wl-custom-text-highlight md:col-end-19 md:col-start-2 md:items-center md:py-0">
        {isFinanceLoading ? (
          <div className="flex justify-center w-full">
            <CircularLoader
              type={
                isAuregoShop(whitelabel)
                  ? CircularLoaderType.secondary
                  : CircularLoaderType.primary
              }
              customSize={
                isAuregoShop(whitelabel) ? CircularLoaderSize.md : undefined
              }
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col">
              <div className="flex flex-col md:flex-row">
                <div className="flex flex-row">
                  <span className="bg-green60 h-[26px] relative rounded-circle w-[26px] monochrome:bg-white">
                    <Calculator16 className="absolute h-[14px] items-center justify-center left-[6px] top-[6px] w-[14px] monochrome:fill-black" />
                  </span>
                  <span className="font-weight-bold ml-[9px] pr-15px pt-[2px] text-green60 monochrome:text-white">
                    {translate(intl, `ui.PDP.FOOTER.FINANCE_TYPE.${payment}`)}
                  </span>
                </div>
                {!isPrivateSeller && (
                  <TextLink
                    text={translate(intl, 'ui.PDP.FOOTER.LINK')}
                    icon={null}
                    isFooter={true}
                    onClick={() => {
                      pushAnalyticsEvent({
                        event: 'choose_payment_type',
                        form_process: 'payment_formStart',
                        product_name: `${carManufacturer} ${carModel}`,
                        product_id: vehicleId,
                        // product_type: 'Schaltgetriebe + Coupe',
                        // product_mileage: '3.513km',
                        // product_1st_registration: '06/2021',
                        // product_fuel_type: 'Benzin',
                        // product_power: '70kw(101 ps)',
                      });
                      // here add ref
                      dispatch(
                        openOverlay({
                          title: translate(intl, 'ui.PAYMENT_OPTIONS.TITLE'),
                          children: (
                            <PaymentOptions
                              vehicleId={vehicleId}
                              vehiclePrice={price}
                              dealerId={configuratorDealerId}
                              carConfiguratorId={carConfiguratorId}
                              financingDetails={financingDetails}
                              selectedPaymentType={payment}
                              cashOptions={cashOptions}
                              financeCalculatorOptions={
                                financeCalculatorOptions
                              }
                              onCashSubmit={onPaymentCashSubmit}
                              onLeasingSubmit={onPaymentLeasingSubmit}
                              onFinancingSubmit={onPaymentFinancingSubmit}
                              setCalculateLoanSummary={setCalculateLoanSummary}
                            />
                          ),
                        })
                      );
                    }}
                    isDarkBg={true}
                    id={'link--change-payment-method'}
                    customClassName={clsx(
                      !isInteractive && 'hidden',
                      'md:flex'
                    )}
                  />
                )}
              </div>
              {!isPrivateSeller &&
                (payment === PaymentType.LeasingRateRange ||
                  payment === PaymentType.LoanRateRange) && (
                  <div
                    className={clsx(
                      'footer-leasing-details h-auto pt-[8px] text-p3 text-white transition-all md:flex monochrome:text-white',
                      !isInteractive && 'h-0 opacity-0 md:h-auto md:opacity-100'
                    )}
                  >
                    {downPaymentString && (
                      <span>
                        <span id="label--down-payment-value">
                          {downPaymentString}
                        </span>
                        <span id="label--down-payment">{`${translate(
                          intl,
                          'ui.PDP.FOOTER.DEPOSIT'
                        )}`}</span>
                      </span>
                    )}
                    {durationString && (
                      <span className="pl-10px">
                        <span id="label--duration-value">{durationString}</span>
                        <span id="label--duration">
                          {translate(intl, 'ui.PDP.FOOTER.MONTHS')}
                        </span>
                      </span>
                    )}
                    {mileageString && (
                      <span className="pl-10px">
                        <span id="label--mileage-value">{mileageString}</span>
                        <span id="label--mileage">
                          {translate(intl, 'ui.PDP.FOOTER.MILEAGE')}
                        </span>
                      </span>
                    )}
                    {payment === PaymentType.LoanRateRange &&
                      finalInstallmentString && (
                        <span className="pl-10px">
                          <span id="label--price">
                            {finalInstallmentString}
                          </span>
                          <span id="label--payment-option">
                            {translate(intl, 'ui.PDP.FOOTER.FINAL_INSTALLMENT')}
                          </span>
                        </span>
                      )}
                  </div>
                )}
            </div>
            <PDPFooterPrice
              paymentType={payment}
              price={price}
              monthlyInstallment={monthlyPrice}
              isPrivateSeller={isPrivateSeller}
              isFixedPrice={isFixedPrice}
              priceLabel={priceLabelText}
            />
          </>
        )}
      </div>
      <div
        className={`${
          isAuregoShop(whitelabel) && 'text-black'
        } add-border bg-red60 bottom-0 col-end-25 cursor-pointer fixed flex flex-1 flex-row h-[50px]
         items-center justify-center mb-15px ml-[4.166%] rounded-default text-white transition-colors 
         w-[91.668%] wl-bg-highlight wl-bg-highlight--hover wl-text-highlight wl-custom-text-highlight 
         sm:col-start-1 md:col-start-20 md:flex md:h-100px md:m-auto md:relative md:rounded-none 
         wl-custom-bg-highlight md:w-full monochrome:bg-black hover:bg-red70`}
        onClick={handlerButtonClick}
      >
        {addToCartLoading || isFinanceLoading ? (
          <CircularLoader
            type={
              isAuregoShop(whitelabel)
                ? CircularLoaderType.secondary
                : CircularLoaderType.primary
            }
            customSize={
              isAuregoShop(whitelabel) ? CircularLoaderSize.md : undefined
            }
          />
        ) : (
          ctaPartial
        )}
      </div>
    </div>
  );
};

export default CarDetailsFooter;
